import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listingsAPI } from '../api/client'
import { useCompare } from '../hooks/useCompare'
import { inBoxLabelKey } from '../utils/inBoxCompleteness'
import { listingCardThumb } from '../utils/listingThumb'
import ListingNoPhotoPlaceholder from './ListingNoPhotoPlaceholder'
import ModalCloseButton from './ModalCloseButton'

interface ListingImage { id: number; thumbnail_url: string; display_url?: string; is_primary: boolean }

interface Listing {
  id: number
  title: string
  price: number
  condition: string
  battery_health?: number
  in_box_completeness?: string
  phone_model: { manufacturer: string; model_name: string; hero_image_path?: string | null; release_year?: number | null }
  images?: ListingImage[]
}

interface CompareModalProps {
  open: boolean
  onClose: () => void
  onOpenListing?: (id: number) => void
}

function thumbFor(l: Listing): { src: string | null; isUserPhoto: boolean } {
  const th = listingCardThumb(l)
  return { src: th.src, isUserPhoto: th.isUserPhoto }
}

export default function CompareModal({ open, onClose, onOpenListing }: CompareModalProps) {
  const { t } = useTranslation()
  const { ids, toggle } = useCompare()
  const [items, setItems] = useState<Listing[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || ids.length === 0) return
    setLoading(true)
    Promise.all(
      ids.map((id) =>
        listingsAPI
          .getListing(id)
          .then((res) => res.data as Listing)
          .catch(() => null),
      ),
    )
      .then((arr) => setItems(arr.filter(Boolean) as Listing[]))
      .finally(() => setLoading(false))
  }, [open, ids])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const scrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      window.scrollTo(0, scrollY)
    }
  }, [open, onClose])

  const bestPrice = useMemo(
    () => (items.length ? Math.min(...items.map((i) => i.price)) : 0),
    [items],
  )
  const bestBattery = useMemo(
    () => (items.length ? Math.max(...items.map((i) => i.battery_health ?? 0)) : 0),
    [items],
  )

  if (!open) return null

  return (
    <div
      className="compare-modal-backdrop compare-modal-backdrop--app"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="compare-modal compare-modal--app"
        role="dialog"
        aria-modal="true"
        aria-labelledby="compare-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{ '--compare-count': items.length } as React.CSSProperties}
      >
        <div className="compare-modal__glow" aria-hidden="true" />
        <ModalCloseButton
          className="compare-modal__close"
          onClick={onClose}
          aria-label={t('compare.close', 'Uždaryti')}
        />

        <header className="compare-modal__head">
          <div className="compare-modal__head-text">
            <p className="compare-modal__eyebrow">{t('compare.eyebrow', 'Palyginimas')}</p>
            <h2 id="compare-modal-title">{t('compare.modalTitle', 'Skelbimų palyginimas')}</h2>
            {items.length > 0 ? (
              <p className="compare-modal__subtitle">{t('compare.intro', { count: items.length })}</p>
            ) : null}
          </div>
        </header>

        {loading && (
          <div className="compare-modal__skeleton" aria-busy="true" aria-label={t('compare.loading', 'Kraunama…')}>
            <span className="compare-modal__skeleton-line compare-modal__skeleton-line--short" />
            <span className="compare-modal__skeleton-line" />
            <span className="compare-modal__skeleton-line compare-modal__skeleton-line--table" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <p className="compare-modal__empty">{t('compare.empty', 'Nepasirinkote skelbimų palyginimui.')}</p>
        )}

        {!loading && items.length > 0 && (
          <div className="compare-modal__body">
            {items.length > 1 ? (
              <p className="compare-legend">
                <span className="compare-legend__dot" aria-hidden="true" />
                {t('compare.legend', 'Žalia spalva — geriausia reikšmė šioje eilutėje')}
              </p>
            ) : null}

            <div className="compare-table-scroll">
              <table className="compare-table">
                <thead>
                  <tr>
                    <th className="compare-table__corner" scope="col" />
                    {items.map((l, idx) => {
                      const th = thumbFor(l)
                      return (
                        <th
                          key={l.id}
                          className="compare-table__phone"
                          scope="col"
                          style={{ animationDelay: `${idx * 0.06}s` }}
                        >
                          <button
                            type="button"
                            className="compare-table__remove"
                            onClick={() => toggle(l.id)}
                            aria-label={t('compare.remove', 'Pašalinti iš palyginimo')}
                          >
                            ×
                          </button>
                          <span className="compare-table__index">
                            {t('compare.phoneIndex', { current: idx + 1, total: items.length })}
                          </span>
                          <div className="compare-table__thumb">
                            {th.src ? (
                              <img src={th.src} alt="" loading="lazy" decoding="async" />
                            ) : (
                              <ListingNoPhotoPlaceholder variant="thumb" />
                            )}
                          </div>
                          <div className="compare-table__name">
                            <strong>{l.phone_model.manufacturer}</strong>
                            <span>{l.phone_model.model_name}</span>
                          </div>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  <tr className="compare-table__row">
                    <th className="compare-table__label" scope="row">
                      {t('compare.rowPrice', 'Kaina')}
                    </th>
                    {items.map((l) => {
                      const best = items.length > 1 && l.price === bestPrice
                      return (
                        <td key={l.id} className={`compare-table__cell${best ? ' compare-table__cell--best' : ''}`}>
                          <span className="compare-table__value">€{l.price.toFixed(2)}</span>
                          {best ? <span className="compare-table__badge">{t('compare.best', 'Geriausia')}</span> : null}
                        </td>
                      )
                    })}
                  </tr>
                  <tr className="compare-table__row">
                    <th className="compare-table__label" scope="row">
                      {t('compare.rowCondition', 'Būklė')}
                    </th>
                    {items.map((l) => (
                      <td key={l.id} className="compare-table__cell">
                        <span className="compare-table__value">{t(`listing.condition.${l.condition}`, l.condition)}</span>
                      </td>
                    ))}
                  </tr>
                  <tr className="compare-table__row">
                    <th className="compare-table__label" scope="row">
                      {t('compare.rowBattery', 'Baterija')}
                    </th>
                    {items.map((l) => {
                      const pct = l.battery_health ?? 0
                      const best = items.length > 1 && pct === bestBattery && bestBattery > 0
                      return (
                        <td key={l.id} className={`compare-table__cell${best ? ' compare-table__cell--best' : ''}`}>
                          <span className="compare-table__value">
                            {l.battery_health != null ? `${l.battery_health}%` : '—'}
                          </span>
                          {best ? <span className="compare-table__badge">{t('compare.best', 'Geriausia')}</span> : null}
                        </td>
                      )
                    })}
                  </tr>
                  <tr className="compare-table__row">
                    <th className="compare-table__label" scope="row">
                      {t('compare.rowKit', 'Komplektacija')}
                    </th>
                    {items.map((l) => (
                      <td key={l.id} className="compare-table__cell">
                        <span className="compare-table__value">
                          {l.in_box_completeness ? t(inBoxLabelKey(l.in_box_completeness)) : '—'}
                        </span>
                      </td>
                    ))}
                  </tr>
                  <tr className="compare-table__row">
                    <th className="compare-table__label" scope="row">
                      {t('compare.rowYear', 'Metai')}
                    </th>
                    {items.map((l) => (
                      <td key={l.id} className="compare-table__cell">
                        <span className="compare-table__value">{l.phone_model.release_year ?? '—'}</span>
                      </td>
                    ))}
                  </tr>
                  <tr className="compare-table__actions-row compare-table__row">
                    <th className="compare-table__label" scope="row" />
                    {items.map((l) => (
                      <td key={l.id} className="compare-table__cell compare-table__cell--action">
                        <button
                          type="button"
                          className="compare-table__open"
                          onClick={() => {
                            onOpenListing?.(l.id)
                            onClose()
                          }}
                        >
                          {t('compare.openListing', 'Atidaryti skelbimą')}
                        </button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
